import React, { useState, useEffect, useContext, Suspense } from "react";
import { Nav } from "react-bootstrap";
/** Components */
//import "./getMapListConfiguration.scss";
//import "./listMapLocator.scss";
import { Helmet } from "react-helmet-async";

/** Utilities */
import { setSecurityParameters } from "../../../services/httpService";
import {
  getValidQuerystringParams,
  getQueryStringParam,
  validRadiusSearches,
  buildSearchParameters,
} from "../../../utils/bullseyeUtils";
import { getUserCountry } from "../../../utils/bullseyeUtils2";
import { SEARCH_TYPE } from "../../../utils/constants";
import { isMobile } from "react-device-detect";

/** Hooks */
import { useParams, useHistory } from "react-router-dom";
import useGeoLocation from "../../../hooks/useGeolocation";
import * as Sentry from "@sentry/react";
import { LanguageContext } from "../../../context/language";
import { SearchContext2 } from "../../../context/search";
import { getCategoryTree } from "../../../services/restSearchService";
import { LocatorConfigurationContext } from "../../../context/locatorConfiguration";
import { ApplicationContext } from "../../../context/application";
import { bullseyeTypeErrors } from "../../../utils";

/** Environment Variables */
const homepage = process.env.REACT_APP_VIRTUAL_PATH;

const ListMapLocator = React.lazy(() => import("../listMapLocator"));
const EventSearchLocator = React.lazy(() =>
  import("../eventSearchLocator/EventSearchLocator")
);

const getCategoriesTree = async () => {
  return await getCategoryTree();
};

const GetMapListConfiguration = (props) => {
  const {
    setLocatorConfiguration,
    locatorConfigurationIsLoading,
    interfaceConfiguration,
    interfaceInternalConfiguration,
    interfaceNotFound,
  } = useContext(LocatorConfigurationContext);

  // Get parameters from URL
  const { interfaceName } = useParams();
  const { setParameters, searchParameters, isLoading } =
    useContext(SearchContext2);

  const queryString = props.location.search;

  Sentry.setTag("interface_name", interfaceName);

  let languageCode = getQueryStringParam(queryString, "langcode");

  const browserLanguage = window.navigator.language;
  const indexBrowserLanguage = browserLanguage.indexOf("-");
  const languageBrowserCode =
    indexBrowserLanguage === -1
      ? browserLanguage
      : browserLanguage.substring(0, indexBrowserLanguage);

  if (!languageCode) {
    languageCode = languageBrowserCode;
  }

  let radius = getQueryStringParam(queryString, "radius");
  let ip = getQueryStringParam(queryString, "ip");
  const interfaceId = getQueryStringParam(queryString, "interfaceId");
  const categories = getQueryStringParam(queryString, [
    "categories",
    "category",
    "categoryids",
  ]);

  const categoryName = getQueryStringParam(queryString, ["categorynames"]);

  const Category3rdPartyIDs = getQueryStringParam(queryString, [
    "category3rdpartyids",
  ]);

  const eventSearchTab = getQueryStringParam(queryString, ["eventsearch"]);

  const [matchCategories, setMatchCategories] = useState({
    loading: true,
    categories: "",
  });

  const [activeTab, setActiveTab] = useState("storelocator");

  const { eventLocatorVisible } = useContext(ApplicationContext);

  const searchParams = getValidQuerystringParams(queryString);

  const { setLabelLanguages, getLabelLanguage } = useContext(LanguageContext);

  /** Convert to int the queryString radius */
  if (radius) {
    radius = parseInt(radius, 10);
  }

  const history = useHistory();

  useEffect(() => {
    setLocatorConfiguration({
      interfaceName: interfaceName,
      apiKeyName: "",
      language: languageCode,
      interfaceId: interfaceId,
    });
  }, []);

  const originPath = () => {
    let currentUrl = new URL(window.location.href);
    let currentPath = currentUrl.pathname;

    if (currentPath.includes("/events")) {
      let newPath = currentPath.replace("/events", "");
      currentUrl.pathname = newPath;

      // Reemplazar la URL sin agregar una nueva entrada en el historial
      window.location.replace(currentUrl.href);
    }
  };
  //Call hook to get geolocation; if no search parameter is passed in
  // https://developers.google.com/web/fundamentals/native-hardware/user-location
  const [geoLocation] = useGeoLocation(
    isMobile,
    interfaceConfiguration &&
      interfaceConfiguration.useIPDetection &&
      Object.keys(searchParams).length === 0 &&
      !searchParams.error &&
      !interfaceConfiguration.maxMindFlag,
    ip,
    !locatorConfigurationIsLoading
  );

  useEffect(() => {
    if (!ip) {
      ip = geoLocation.coordinates.ip;
    }

    if (
      !locatorConfigurationIsLoading &&
      interfaceConfiguration &&
      interfaceConfiguration.clientId > 0 &&
      interfaceConfiguration.apiKey
    ) {
      setSecurityParameters(
        interfaceConfiguration.clientId,
        interfaceConfiguration.apiKey
      );
    }

    const eventUrlIs = window.location.pathname.includes("/events");

    if (eventUrlIs && String(eventSearchTab).toLowerCase() === "true") {
      setActiveTab("eventsearch");
    } else if (eventUrlIs) {
      setActiveTab("eventsearch");
    }
  }, [locatorConfigurationIsLoading]);

  let selectedCategoriesRequired = true;

  // If any of the category filter is mandatory don't make the autosearch
  let categoryRequired =
    interfaceConfiguration?.interfaceCategoryGroups?.filter(
      (x) => x.filterRequired
    ).length > 0;

  // If there are categories and one is required, this will be false.
  if (matchCategories.categories === null && categoryRequired) {
    selectedCategoriesRequired = false;
  }

  if (!matchCategories.loading && matchCategories.categories?.length > 0) {
    const categoryIds = matchCategories.categories.split(",");
    const categoryIdsInt = categoryIds.map((id) => parseInt(id, 10));

    // Validates if any category sent from the url exists, if it does not exist, not a single category was selected.
    interfaceInternalConfiguration.categoryGroups?.forEach((cat) => {
      if (cat.required) {
        const matchFound = cat.categories.some((subCat) =>
          categoryIdsInt.includes(subCat.id)
        );

        if (!matchFound) {
          selectedCategoriesRequired = false;
        }
      }
    });
  }

  // This is trigged when mobile/GPS is enabled
  useEffect(() => {
    const matchingCategoriesIDs = [];

    function subCategoriesMatch(subCategories) {
      if (!subCategories) return;

      for (const subCategory of subCategories) {
        if (
          (categoryName &&
            categoryName
              .split(",")
              .some((name) =>
                subCategory.Name.toLowerCase().includes(
                  name.trim().toLowerCase()
                )
              )) ||
          (Category3rdPartyIDs &&
            Category3rdPartyIDs.split(",").some((partyids) =>
              subCategory.ThirdPartyID.toLowerCase().includes(
                partyids.trim().toLowerCase()
              )
            ))
        ) {
          matchingCategoriesIDs.push(subCategory.ID.toString());
        }

        if (subCategory.SubCategories) {
          subCategoriesMatch(subCategory.SubCategories);
        }
      }
    }

    const fillCategoriesQueryParameters = async () => {
      if (
        interfaceConfiguration &&
        interfaceConfiguration.clientId > 0 &&
        interfaceConfiguration.apiKey &&
        matchCategories.loading
      ) {
        if (categoryName || Category3rdPartyIDs) {
          try {
            const categories = await getCategoriesTree();

            if (categories) {
              for (const category of categories) {
                subCategoriesMatch(category.SubCategories);

                if (
                  (categoryName &&
                    categoryName
                      .split(",")
                      .some((name) =>
                        category.Name.toLowerCase().includes(
                          name.trim().toLowerCase()
                        )
                      )) ||
                  (Category3rdPartyIDs &&
                    Category3rdPartyIDs.split(",").some((partyids) =>
                      category.ThirdPartyID.toLowerCase().includes(
                        partyids.trim().toLowerCase()
                      )
                    ))
                ) {
                  matchingCategoriesIDs.push(category.ID.toString()); // Add the subcategory to matchingCategoriesIDs if there is a match
                }
              }
            }
          } catch (error) {
            console.error("Error retrieving category tree:", error);
          }
        }

        let categoriesParametersMatch = categories;

        if (matchingCategoriesIDs.length > 0) {
          if (categoriesParametersMatch) {
            categoriesParametersMatch =
              categoriesParametersMatch +
              "," +
              matchingCategoriesIDs.join(",").toString(); // Combine the IDs of the found categories with the previous IDs of matchingCategoriesIDs
          } else {
            categoriesParametersMatch = matchingCategoriesIDs
              .join(",")
              .toString();
          }
        }

        setMatchCategories({
          loading: false,
          categories: categoriesParametersMatch,
        });
      }
    };

    fillCategoriesQueryParameters();

    /** if we still don't have the configuration do nothing */
    if (
      locatorConfigurationIsLoading ||
      !geoLocation.loaded ||
      matchCategories.loading
    )
      return;

    if (isMobile && isLoading) {
      const [countryFromUser, countryRegion] = getUserCountry(
        geoLocation.coordinates?.country,
        geoLocation.coordinates?.country,
        0,
        interfaceConfiguration.countries,
        interfaceConfiguration.countryRegions
      );

      if (
        !interfaceConfiguration.useIPDetection &&
        !locatorConfigurationIsLoading
      ) {
        setParameters({
          countryId: null,
          radius: radius ?? interfaceInternalConfiguration?.selectedRadius,
          loading: false,
          ready:
            interfaceConfiguration.autoSearch && selectedCategoriesRequired,
          postalCode: null,
          latitude: null,
          longitude: null,
          city: null,
          state: null,
          searchDisplayText: null,
          categoryIDs: matchCategories.categories,
          locationGroupIds:
            interfaceConfiguration.locationGroups?.length > 0
              ? interfaceConfiguration.locationGroups.map(
                  (x) => x.locationGroupID
                )
              : null,
          userIPAddress: ip,
          _reference: "A1",
          isManualSearch: false,
          searchTypeId: countryRegion?.searchTypeId,
        });
      }

      if (
        countryRegion?.searchTypeId !== SEARCH_TYPE.radius &&
        countryRegion !== undefined
      ) {
        setActiveTab("storelocator");
        originPath();
      }

      if (
        !interfaceConfiguration.showAllLocations &&
        interfaceConfiguration.showAllLocationsPerCountry &&
        !interfaceConfiguration.showNearestLocationsInList &&
        countryRegion?.searchTypeId === SEARCH_TYPE.radius
      ) {
        console.log("Search by country activate");
        setParameters({
          countryId: countryFromUser,
          radius: null,
          loading: false,
          ready:
            interfaceConfiguration.autoSearch && selectedCategoriesRequired,
          postalCode: null,
          latitude: null,
          longitude: null,
          city: null,
          state: null,
          searchDisplayText: null, //currentPosition.formatted_address,
          searchTypeId: countryRegion?.searchTypeId,
          categoryIDs: matchCategories.categories,
          userIPAddress: ip,
          _reference: "A7",
          isManualSearch: false,
        });
      } else {
        let searchDisplayText = "";
        if (
          !interfaceConfiguration.useZipCode &&
          geoLocation.coordinates?.city &&
          geoLocation.coordinates?.state
        ) {
          searchDisplayText =
            geoLocation.coordinates.city + ", " + geoLocation.coordinates.state;
        } else if (geoLocation.coordinates?.postalCode) {
          searchDisplayText = geoLocation.coordinates.postalCode;
        }

        setParameters({
          countryId: countryFromUser,
          radius: radius ?? interfaceInternalConfiguration?.selectedRadius,
          loading: false,
          ready:
            interfaceConfiguration.autoSearch && selectedCategoriesRequired,
          postalCode: null,
          latitude: validRadiusSearches(countryRegion)
            ? geoLocation.coordinates.lat
            : null,
          longitude: validRadiusSearches(countryRegion)
            ? geoLocation.coordinates.lng
            : null,
          city: null, // currentPosition.city,
          state: null, //currentPosition.state,
          searchDisplayText: searchDisplayText, //  currentPosition.formatted_address,
          categoryIDs: matchCategories.categories,
          locationGroupIds:
            interfaceConfiguration.locationGroups?.length > 0
              ? interfaceConfiguration.locationGroups.map(
                  (x) => x.locationGroupID
                )
              : null,
          userIPAddress: ip,
          _reference: "A2",
          isManualSearch: false,
          searchTypeId: countryRegion?.searchTypeId,
        });
      }
    }
  }, [
    geoLocation.loaded,
    locatorConfigurationIsLoading,
    matchCategories.loading,
  ]);

  useEffect(() => {
    /** if we still don't have the configuration do nothing */
    if (locatorConfigurationIsLoading || matchCategories.loading) return;

    // If no countries are specify in the configuration that means,
    // there is locations in the account or for this interface this
    // will redirect to the Not Found locations  error page
    if (interfaceConfiguration.countries.length === 0) {
      history.push(homepage + "error/no-locations");
    }
    console.log("language Code:", languageCode ?? "en");
    setLabelLanguages(
      languageCode ?? "en",
      interfaceConfiguration.labelLanguage[0].labels
    );

    /** We are going to set parameters using  */
    if (geoLocation.loaded) {
      // This will search if the countryID in the pulled from querystring or
      // the detected country in the maxmind exists in the configuration countries
      let countryFromUser = "";
      let countryRegion = null;

      if (
        geoLocation.coordinates.country ||
        geoLocation.coordinates.countryName ||
        searchParams?.countryId
      ) {
        /** Search in the configuration if the user country or the country in
         * the Querystring is configured
         */
        const countryFromConfig = interfaceConfiguration.countries.find((c) => {
          if (searchParams?.countryId) return c.id === searchParams?.countryId;

          return (
            c.name === geoLocation.coordinates.countryName ||
            c.code === geoLocation.coordinates?.country
          );
        });

        if (countryFromConfig) {
          countryFromUser = countryFromConfig.id;
        } else if (interfaceConfiguration.countries.length === 1) {
          countryFromUser = interfaceConfiguration.countries[0].id;
        }

        countryRegion = interfaceConfiguration.countryRegions.find(
          (x) => x.countryId === countryFromUser
        );

        if (
          countryRegion?.searchTypeId !== SEARCH_TYPE.radius &&
          countryRegion !== undefined
        ) {
          setActiveTab("storelocator");
          originPath();
        }
      } else if (
        (geoLocation.hasError === true &&
          interfaceConfiguration.countries.length === 1) ||
        (isMobile && interfaceConfiguration.countries.length === 1)
      ) {
        // this if will happend if the user block the GPS on mobile
        // and we only have one country or if gelocation gets other error like ip error
        countryFromUser = interfaceConfiguration.countries[0].id;

        countryRegion = interfaceConfiguration.countryRegions.find(
          (x) => x.countryId === countryFromUser
        );
      } else if (geoLocation.hasError === true) {
      }

      // This mean we have default parameters and we won't use any of the
      // Parameters on the user location
      if (Object.keys(searchParams).length > 0) {
        let searchDisplayText = "";

        if (searchParams?.city || searchParams?.state) {
          if (searchParams?.city && searchParams?.state) {
            searchDisplayText = searchParams?.city + ", " + searchParams?.state;
          } else {
            searchDisplayText = searchParams?.city ?? searchParams?.state;
          }
        } else {
          searchDisplayText = searchParams?.postalCode;
        }

        setParameters({
          postalCode: searchParams?.postalCode,
          countryId: countryFromUser,
          radius:
            searchParams?.radius ??
            radius ??
            interfaceInternalConfiguration?.selectedRadius,
          keyword: searchParams?.keyword,
          city: searchParams?.city,
          state: searchParams?.state,
          distanceUnit: searchParams?.distanceUnit,
          searchDisplayText: searchDisplayText,
          latitude: null,
          longitude: null,
          loading: false,
          ready: true,
          categoryIDs: matchCategories.categories,
          locationGroupIds:
            interfaceConfiguration.locationGroups?.length > 0
              ? interfaceConfiguration.locationGroups.map(
                  (x) => x.locationGroupID
                )
              : null,
          userIPAddress: ip,
          _reference: "A3",
          isManualSearch: false,
          searchTypeId: null,
        });
      } else {
        if (
          isMobile &&
          (geoLocation.coordinates.lat !== 0 ||
            geoLocation.coordinates.lng !== 0)
        ) {
          return;
        }

        let searchDisplayText = "";

        if (
          !interfaceConfiguration.useZipCode &&
          geoLocation.coordinates?.city &&
          geoLocation.coordinates?.state
        ) {
          searchDisplayText =
            geoLocation.coordinates.city + ", " + geoLocation.coordinates.state;
        } else if (geoLocation.coordinates?.postalCode) {
          searchDisplayText = geoLocation.coordinates.postalCode;
        }

        // This will show trigger a country search for the list
        if (
          interfaceConfiguration.showAllLocationsPerCountry &&
          interfaceConfiguration.showNearestLocationsInList &&
          countryRegion?.searchTypeId === SEARCH_TYPE.radius
        ) {
          console.log("Search by country activate");

          setParameters({
            countryId: countryFromUser,
            radius: radius ?? interfaceInternalConfiguration?.selectedRadius,
            loading: false,
            ready:
              interfaceConfiguration.autoSearch && selectedCategoriesRequired,
            postalCode:
              interfaceConfiguration.useZipCode &&
              countryRegion?.searchTypeId !== SEARCH_TYPE.country
                ? geoLocation.coordinates.postalCode
                : null,
            latitude: validRadiusSearches(countryRegion)
              ? geoLocation.coordinates.lat
              : null,
            longitude: validRadiusSearches(countryRegion)
              ? geoLocation.coordinates.lng
              : null,
            city: geoLocation.coordinates.city,
            state: geoLocation.coordinates.state,
            searchDisplayText: searchDisplayText,
            categoryIDs: matchCategories.categories,
            locationGroupIds:
              interfaceConfiguration.locationGroups?.length > 0
                ? interfaceConfiguration.locationGroups.map(
                    (x) => x.locationGroupID
                  )
                : null,
            searchTypeId: countryRegion?.searchTypeId,
            userIPAddress: ip,
            _reference: "A6",
            isManualSearch: false,
          });
        } else {
          /* If there is not parameters we are going to send only the Lat and Lon
           *  detected on the getLocation using the MaxMind */
          setParameters({
            countryId: countryFromUser,
            radius: radius ?? interfaceInternalConfiguration?.selectedRadius,
            loading: false,
            ready:
              interfaceConfiguration.autoSearch && selectedCategoriesRequired,
            postalCode:
              interfaceConfiguration.useZipCode &&
              countryRegion?.searchTypeId !== SEARCH_TYPE.country
                ? geoLocation.coordinates.postalCode
                : null,
            latitude: validRadiusSearches(countryRegion)
              ? geoLocation.coordinates.lat
              : null,
            longitude: validRadiusSearches(countryRegion)
              ? geoLocation.coordinates.lng
              : null,
            city: !interfaceConfiguration.useZipCode
              ? geoLocation.coordinates.city
              : null,
            state: !interfaceConfiguration.useZipCode
              ? geoLocation.coordinates.state
              : null,
            searchDisplayText: searchDisplayText,
            categoryIDs: matchCategories.categories,
            locationGroupIds:
              interfaceConfiguration.locationGroups?.length > 0
                ? interfaceConfiguration.locationGroups.map(
                    (x) => x.locationGroupID
                  )
                : null,
            searchTypeId: countryRegion?.searchTypeId,
            userIPAddress: ip,
            _reference: "A4",
            isManualSearch: false,
          });
        }
      }
    }
  }, [
    geoLocation.loaded,
    locatorConfigurationIsLoading,
    matchCategories.loading,
  ]);

  const handleTabChange = (tab) => {
    let searchParametersPath = {};

    const isNotRadiusCountry = interfaceConfiguration?.countryRegions.some(
      (countries) => {
        return (
          countries.countryId === searchParameters.countryId &&
          countries.searchTypeId !== SEARCH_TYPE.radius
        );
      }
    );

    if (!isNotRadiusCountry) {
      if (
        searchParameters.postalCode ||
        searchParameters.city ||
        searchParameters.state
      ) {
        searchParametersPath = { ...searchParameters };
      }
    }
    delete searchParametersPath._reference;
    delete searchParametersPath.isManualSearch;
    delete searchParametersPath.ready;
    delete searchParametersPath.searchTypeId;

    let eventSearchParam = "eventsearch";

    let searchParams = new URLSearchParams(window.location.search);

    searchParams.delete(eventSearchParam);

    const searchParametersItems = buildSearchParameters(
      searchParams.toString(),
      searchParametersPath,
      languageCode
    );

    setActiveTab(tab);

    let currentPath = window.location.pathname;
    if (
      searchParameters.searchTypeId !== SEARCH_TYPE.radius &&
      !currentPath.includes("/events")
    ) {
      delete searchParameters.countryId;
    }

    if (tab === "eventsearch" && !currentPath.includes("/events")) {
      history.push(`${currentPath}/events${searchParametersItems}`);
    } else if (tab === "storelocator" && currentPath.includes("/events")) {
      let newPath = currentPath.replace("/events", "");
      history.push(`${newPath}${searchParametersItems}`);
    }
    window.location.reload();
  };

  return !locatorConfigurationIsLoading && interfaceNotFound
    ? (window.location.pathname = `${homepage}/error/not-configured`)
    : !locatorConfigurationIsLoading &&
        !matchCategories.loading &&
        geoLocation.loaded &&
        !isLoading &&
        (!interfaceConfiguration?.enableEventSearch &&
        window.location.pathname.includes("/events") ? (
          (window.location.pathname = `${homepage}/error/notFound`)
        ) : (
          <>
            <Helmet>
              <style>{`${interfaceConfiguration.basicStyleCSS}`}</style>
              <style>{`${interfaceConfiguration.advancedStyleCSS}`}</style>
              <link
                rel="canonical"
                href={`${window.location.href.slice(
                  0,
                  window.location.href.indexOf("?")
                )}`}
              />
              <script>{`try { ${interfaceConfiguration.advancedStyleJS} } catch (error) { error.issueType = "${bullseyeTypeErrors.advancedStyle}"; throw error; }`}</script>

              {interfaceInternalConfiguration.faviconFileUrl ? (
                <link
                  rel="icon"
                  href={interfaceInternalConfiguration.faviconFileUrl}
                  type="image/png"
                />
              ) : (
                <link
                  rel="icon"
                  href={`${homepage}favicon.ico`}
                  type="image/x-icon"
                />
              )}
            </Helmet>
            {interfaceConfiguration?.enableEventSearch ? (
              <div className="container">
                <div className="container-fluid custom-header-class pt-4">
                  <div className="">
                    <div className="col-12 bl-no-gutter-sm px-0">
                      {interfaceConfiguration.enableCustomHeaderFooter &&
                        interfaceConfiguration.headerCode && (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: interfaceConfiguration.headerCode,
                            }}
                          />
                        )}
                    </div>
                  </div>
                </div>
                <Nav variant="tabs" className="m-3">
                  <Nav.Item
                    className={`${
                      activeTab !== "storelocator"
                        ? ""
                        : "bullseye_border_button_nav"
                    }`}
                  >
                    <Nav.Link
                      eventKey="storelocator"
                      active={activeTab === "storelocator"}
                      onClick={() => handleTabChange("storelocator")}
                      style={{
                        backgroundColor:
                          activeTab === "storelocator"
                            ? "#f5f5f5"
                            : "lightgray",
                      }}
                      className={`${
                        activeTab !== "storelocator"
                          ? ""
                          : "bullseye_border_button_nav"
                      }`}
                    >
                      <label
                        style={{
                          color: activeTab !== "storelocator" ? "gray" : "",
                        }}
                        className={`bold ${
                          activeTab !== "storelocator" ? "" : "bullseye-red"
                        }`}
                      >
                        Store Locator
                      </label>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item
                    className={`${
                      activeTab !== "eventsearch"
                        ? ""
                        : "bullseye_border_button_nav"
                    }`}
                  >
                    <Nav.Link
                      className={`${eventLocatorVisible ? "" : "disabled"}`}
                      eventKey="eventsearch"
                      active={activeTab === "eventsearch"}
                      onClick={() => handleTabChange("eventsearch")}
                      style={{
                        backgroundColor:
                          activeTab === "eventsearch" ? "#f5f5f5" : "lightgray",
                      }}
                    >
                      <label
                        style={{
                          color: activeTab !== "eventsearch" ? "gray" : "",
                        }}
                        className={`bold ${
                          activeTab !== "eventsearch" ? "" : "bullseye-red"
                        }`}
                      >
                        {getLabelLanguage("EventLocator", "Event Locator")}
                      </label>
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
                <div className="tab-content">
                  {activeTab === "storelocator" ? (
                    <div
                      className={`tab-pane fade ${
                        activeTab === "storelocator" ? "show active" : ""
                      }`}
                    >
                      <Suspense fallback={<></>}>
                        <ListMapLocator />
                      </Suspense>
                    </div>
                  ) : (
                    <div
                      className={`tab-pane fade ${
                        activeTab === "eventsearch" ? "show active" : ""
                      }`}
                    >
                      <Suspense fallback={<></>}>
                        <EventSearchLocator />
                      </Suspense>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <Suspense fallback={<></>}>
                <ListMapLocator />
              </Suspense>
            )}
          </>
        ));
};

export default GetMapListConfiguration;
